
import React, { useState, useEffect } from "react";
import {
  ArrowLeft,
  Mail,
  Phone,
  Clock,
  FileText,
  TrendingUp,
  Award,
  Loader2,
  X,
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import {
  getEmployeeData,
  getMyLeaveBalance,
  getMyLeaves,
} from "../../services/hrms.service";

const EmployeeProfile = () => {
  const navigate = useNavigate();
  const { employeeId } = useParams();

  const [employee, setEmployee] = useState(null);
  const [workHistory, setWorkHistory] = useState([]);
  const [familyData, setFamilyData] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [leaveHistory, setLeaveHistory] = useState([]);
  const [showLeaveHistoryModal, setShowLeaveHistoryModal] = useState(false);
  const [leaveLoading, setLeaveLoading] = useState(false);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    attendanceRate: 95,
    totalLeaves: 0,
    leavesUsed: 0,
    leavesRemaining: 0,
    workingHoursThisMonth: 160,
    overtimeHours: 0,
    performanceScore: 92,
  });

  const formatDisplayDate = (value, fallback = "Not available") =>
    value
      ? new Date(value).toLocaleDateString("en-IN", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        })
      : fallback;

  const formatDateRange = (start, end) => {
    if (!start && !end) return "Not available";
    const from = formatDisplayDate(start);
    const to = formatDisplayDate(end);
    return from === to ? from : `${from} - ${to}`;
  };

  const formatDays = (value) =>
    Number(value || 0).toLocaleString("en-IN", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });

  const normalizeStatusKey = (status = "") => {
    const normalized = status.toLowerCase();
    if (normalized === "approved") return "success";
    if (normalized === "rejected" || normalized === "declined")
      return "rejected";
    return "pending";
  };

  const getStatusBadgeClasses = (status = "") => {
    const key = normalizeStatusKey(status);
    if (key === "success") return "bg-emerald-100 text-emerald-700";
    if (key === "rejected") return "bg-rose-100 text-rose-700";
    return "bg-amber-100 text-amber-700";
  };

  const fetchLeaveInsights = async (employeeDbId) => {
    if (!employeeDbId) return;
    try {
      setLeaveLoading(true);
      const [balanceResp, recentResp, historyResp] = await Promise.all([
        getMyLeaveBalance({ employeeId: employeeDbId }),
        getMyLeaves({ page: 1, limit: 5, employeeId: employeeDbId }),
        getMyLeaves({ page: 1, limit: 100, employeeId: employeeDbId }),
      ]);

      if (balanceResp?.status === 1 && Array.isArray(balanceResp.data)) {
        const breakdown = balanceResp.data.map((item) => ({
          id: item.id || item.leave_type_id,
          name: item.leave_type_name || item.code || "Leave",
          code: item.code || "",
          balance: parseFloat(item.balance || 0),
          used: parseFloat(item.used || 0),
        }));

        const remaining = breakdown.reduce(
          (sum, item) => sum + (item.balance || 0),
          0
        );
        const used = breakdown.reduce(
          (sum, item) => sum + (item.used || 0),
          0
        );

        setStats((prev) => ({
          ...prev,
          totalLeaves: remaining + used,
          leavesRemaining: remaining,
          leavesUsed: used,
        }));
      } else {
        setStats((prev) => ({
          ...prev,
          totalLeaves: 0,
          leavesRemaining: 0,
          leavesUsed: 0,
        }));
      }

      const mapLeaves = (data = []) =>
        data.map((leave) => ({
          id: leave.id,
          type: leave.leave_type_name || "Leave",
          reason: leave.reason || "",
          startDate: leave.start_date,
          endDate: leave.end_date,
          status: leave.status || "Pending",
          totalDays: leave.total_days || 1,
          appliedOn: leave.created_at,
        }));

      if (recentResp?.status === 1 && Array.isArray(recentResp.data)) {
        const formatted = mapLeaves(recentResp.data).map((leave) => ({
          id: leave.id,
          type: leave.type,
          title: leave.reason
            ? `${leave.type} • ${leave.reason}`
            : leave.type,
          date: formatDateRange(leave.startDate, leave.endDate),
          appliedOn: formatDisplayDate(leave.appliedOn, "N/A"),
          status: leave.status,
          totalDays: leave.totalDays,
        }));
        setRecentActivity(formatted);
      } else {
        setRecentActivity([]);
      }

      if (historyResp?.status === 1 && Array.isArray(historyResp.data)) {
        const formattedHistory = mapLeaves(historyResp.data).map((leave) => ({
          ...leave,
          startFormatted: formatDisplayDate(leave.startDate, "N/A"),
          endFormatted: formatDisplayDate(leave.endDate, "N/A"),
          appliedOnFormatted: formatDisplayDate(leave.appliedOn, "N/A"),
        }));
        setLeaveHistory(formattedHistory);
      } else {
        setLeaveHistory([]);
      }
    } catch (err) {
      console.error("Error fetching leave data:", err);
      setRecentActivity([]);
      setLeaveHistory([]);
      setStats((prev) => ({
        ...prev,
        totalLeaves: 0,
        leavesRemaining: 0,
        leavesUsed: 0,
      }));
    } finally {
      setLeaveLoading(false);
    }
  };

  // Fetch employee details on component mount
  useEffect(() => {
    const fetchEmployeeDetails = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await getEmployeeData(employeeId);
        if (response.status === 1 && response.data) {
          const empData = response.data.employeeData || {};

          const fullName =
            (empData.employee_name ||
              `${empData.first_name || ""} ${empData.last_name || ""}`.trim()) ||
            "Not available";

          const statusText = empData.status || "Active";

          setEmployee({
            id: empData.id ?? "",
            name: fullName,
            empId: empData.employee_id || "Not available",
            email: empData.email || "Not available",
            phone:
              empData.contact_number ||
              empData.phone ||
              empData.contact ||
              "Not available",
            designation:
              empData.joining_position ||
              empData.designation ||
              "Not available",
            department: empData.department || "Not available",
            status: statusText,
            statusColor: statusText.toLowerCase().includes("leave")
              ? "text-orange-600"
              : "text-green-600",
            joiningDate: formatDisplayDate(empData.joining_date),
            reportingManager: empData.reporting_manager || "Not available",
            workLocation: empData.work_location || "Not available",
            employmentType:
              empData.employee_type ||
              empData.employment_type ||
              "Not available",
            shiftTiming: empData.shift_timing || "Not specified",
            highestQualification: empData.highest_qualification || "Not available",
            bloodGroup: empData.blood_group || "Not available",
          });

          setWorkHistory(response.data.workingCompany || []);
          setFamilyData(response.data.familyData || []);
          if (empData.id) {
            fetchLeaveInsights(empData.id);
          }
        } else {
          setError("Employee not found");
        }
      } catch (err) {
        console.error("Error fetching employee details:", err);
        setError("Failed to load employee details. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    if (employeeId) {
      fetchEmployeeDetails();
    }
  }, [employeeId]);

  if (loading || !employee) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-indigo-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading employee profile...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">
            Error Loading Profile
          </h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="bg-indigo-600 text-white px-6 py-2 rounded-lg hover:bg-indigo-700 transition"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate("/manager/team-members")}
            className="flex items-center text-indigo-600 hover:text-indigo-700 mb-4 font-medium"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Team Members
          </button>
        </div>

        {/* Profile Header */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <div className="flex items-center space-x-6">
            <div className="w-24 h-24 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full flex items-center justify-center text-white text-3xl font-bold">
              {employee.name
                .split(" ")
                .map((n) => n[0])
                .join("")
                .toUpperCase()}
            </div>
            <div className="flex-1">
              <h1 className="text-3xl font-bold text-gray-800 mb-2">
                {employee.name}
              </h1>
              <p className="text-lg text-gray-600 mb-2">
                {employee.designation}
              </p>
              <div className="flex items-center space-x-4">
                <span
                  className={`inline-flex items-center text-sm font-medium ${employee.statusColor}`}
                >
                  ● {employee.status}
                </span>
                <span className="text-sm text-gray-500">
                  Employee ID: {employee.empId}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Personal Details */}
          <div className="lg:col-span-1 space-y-6">
            {/* Contact Information */}
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h2 className="text-lg font-bold text-gray-800 mb-4">
                Contact Information
              </h2>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <Mail className="w-5 h-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Email</p>
                    <p className="text-sm font-medium text-gray-800">
                      {employee.email}
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Phone className="w-5 h-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Phone</p>
                    <p className="text-sm font-medium text-gray-800">
                      {employee.phone}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Employment Details */}
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h2 className="text-lg font-bold text-gray-800 mb-4">
                Employment Details
              </h2>
              <div className="space-y-4">
                <div>
                  <p className="text-xs text-gray-500 mb-1">Department</p>
                  <p className="text-sm font-medium text-gray-800">
                    {employee.department}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">
                    Reporting Manager
                  </p>
                  <p className="text-sm font-medium text-gray-800">
                    {employee.reportingManager}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">Joining Date</p>
                  <p className="text-sm font-medium text-gray-800">
                    {employee.joiningDate}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">Work Location</p>
                  <p className="text-sm font-medium text-gray-800">
                    {employee.workLocation}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">Employment Type</p>
                  <p className="text-sm font-medium text-gray-800">
                    {employee.employmentType}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">Shift Timing</p>
                  <p className="text-sm font-medium text-gray-800">
                    {employee.shiftTiming}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">Highest Qualification</p>
                  <p className="text-sm font-medium text-gray-800">
                    {employee.highestQualification}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">Blood Group</p>
                  <p className="text-sm font-medium text-gray-800">
                    {employee.bloodGroup}
                  </p>
                </div>
              </div>
            </div>

          {/* Work Experience */}
          {workHistory.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h2 className="text-lg font-bold text-gray-800 mb-4">
                Work Experience
              </h2>
              <div className="space-y-4">
                {workHistory.map((job) => (
                  <div
                    key={`${job.id}-${job.company_name}`}
                    className="border rounded-lg p-4"
                  >
                    <p className="text-sm font-semibold text-gray-900">
                      {job.company_name || "Company not specified"}
                    </p>
                    <p className="text-xs text-gray-500 mb-2">
                      {job.job_title || "Role not specified"}
                    </p>
                    <div className="text-xs text-gray-600">
                      <p>
                        Start:{" "}
                        {job.start_date
                          ? new Date(job.start_date).toLocaleDateString("en-IN")
                          : "N/A"}
                      </p>
                      <p>
                        End:{" "}
                        {job.end_date
                          ? new Date(job.end_date).toLocaleDateString("en-IN")
                          : "N/A"}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Family Details */}
          {familyData.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h2 className="text-lg font-bold text-gray-800 mb-4">
                Family Details
              </h2>
              <div className="space-y-4">
                {familyData.map((member) => (
                  <div key={member.id} className="border rounded-lg p-4">
                    <p className="text-sm font-semibold text-gray-900">
                      {member.name || "Name not specified"}
                    </p>
                    <p className="text-xs text-gray-500">
                      {member.relationship || "Relationship not specified"}
                    </p>
                    <p className="text-xs text-gray-500">
                      Contact: {member.contact_number || "N/A"}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}
          </div>

          {/* Right Column - Stats & Activity */}
          <div className="lg:col-span-2 space-y-6">
            {/* Stats Cards - REMOVED EXPECTED HOURS, ADDED PERFORMANCE */}
            <div className="grid grid-cols-2 gap-4">
              {/* Attendance Rate */}
              <div className="bg-white rounded-lg shadow-sm border p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-blue-600" />
                  </div>
                  <span className="text-3xl font-bold text-blue-700">
                    {stats.attendanceRate}%
                  </span>
                </div>
                <h3 className="text-sm font-semibold text-gray-800">
                  Attendance Rate
                </h3>
                <p className="text-xs text-gray-500 mt-1">This month</p>
              </div>

              {/* Leave Balance */}
              <div className="bg-white rounded-lg shadow-sm border p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                    <FileText className="w-6 h-6 text-green-600" />
                  </div>
                  <span className="text-3xl font-bold text-green-700">
                    {formatDays(stats.leavesRemaining)}
                  </span>
                </div>
                <h3 className="text-sm font-semibold text-gray-800">
                  Leaves Remaining
                </h3>
                <p className="text-xs text-gray-500 mt-1">
                  {formatDays(stats.leavesUsed)} of{" "}
                  {formatDays(stats.totalLeaves)} used
                </p>
              </div>

              {/* Working Hours */}
              <div className="bg-white rounded-lg shadow-sm border p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                    <Clock className="w-6 h-6 text-purple-600" />
                  </div>
                  <span className="text-3xl font-bold text-purple-700">
                    {stats.workingHoursThisMonth}h
                  </span>
                </div>
                <h3 className="text-sm font-semibold text-gray-800">
                  Working Hours
                </h3>
                <p className="text-xs text-gray-500 mt-1">
                  +{stats.overtimeHours}h overtime
                </p>
              </div>

              {/* Performance Score - NEW (Replacing Expected Hours) */}
              <div className="bg-white rounded-lg shadow-sm border p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                    <Award className="w-6 h-6 text-orange-600" />
                  </div>
                  <span className="text-3xl font-bold text-orange-700">
                    {stats.performanceScore}%
                  </span>
                </div>
                <h3 className="text-sm font-semibold text-gray-800">
                 Present Days 
                </h3>
                <p className="text-xs text-gray-500 mt-1">Current Month</p>
              </div>
            </div>

            {/* Recent Activity */}
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h2 className="text-lg font-bold text-gray-800 mb-4">
                Recent Activity
              </h2>
              <div className="space-y-3">
                {leaveLoading ? (
                  <div className="flex items-center justify-center py-6 text-gray-500">
                    <Loader2 className="w-5 h-5 mr-2 animate-spin text-indigo-600" />
                    Loading leave activity...
                  </div>
                ) : recentActivity.length === 0 ? (
                  <p className="text-sm text-gray-500">
                    No recent leave applications found.
                  </p>
                ) : (
                  recentActivity.map((activity) => {
                    const statusKey = normalizeStatusKey(activity.status || "");
                    const badgeClasses =
                      statusKey === "pending"
                        ? "bg-yellow-100 text-yellow-700"
                        : statusKey === "success"
                        ? "bg-green-100 text-green-700"
                        : "bg-red-100 text-red-700";
                    const iconWrapper =
                      statusKey === "pending"
                        ? "bg-yellow-100"
                        : statusKey === "success"
                        ? "bg-green-100"
                        : "bg-red-100";
                    const iconColor =
                      statusKey === "pending"
                        ? "text-yellow-600"
                        : statusKey === "success"
                        ? "text-green-600"
                        : "text-red-600";
                    return (
                      <div
                        key={activity.id}
                        className="flex items-center space-x-4 p-3 rounded-lg hover:bg-gray-50 transition"
                      >
                        <div
                          className={`w-10 h-10 rounded-lg flex items-center justify-center ${iconWrapper}`}
                        >
                          <FileText className={`w-5 h-5 ${iconColor}`} />
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-800">
                            {activity.title}
                          </p>
                          <p className="text-xs text-gray-500">
                            {activity.date}
                          </p>
                          <p className="text-xs text-gray-400">
                            Applied on {activity.appliedOn}
                          </p>
                        </div>
                        <span
                          className={`text-xs font-semibold px-2 py-1 rounded-full ${badgeClasses}`}
                        >
                          {activity.status || "Pending"}
                        </span>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex space-x-4">
              <button className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white font-medium py-3 px-6 rounded-lg transition">
                View Full Attendance
              </button>
              <button
                className={`flex-1 bg-white hover:bg-gray-50 text-gray-700 font-medium py-3 px-6 rounded-lg border-2 border-gray-200 transition ${
                  leaveHistory.length === 0 ? "opacity-60 cursor-not-allowed" : ""
                }`}
                onClick={() =>
                  leaveHistory.length > 0 && setShowLeaveHistoryModal(true)
                }
                disabled={leaveHistory.length === 0}
              >
                View Leave History
              </button>
            </div>
          </div>
        </div>
      </div>
      {showLeaveHistoryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-4xl max-h-[80vh] overflow-hidden">
            <div className="flex items-center justify-between border-b px-6 py-4">
              <div>
                <h3 className="text-xl font-semibold text-gray-800">
                  Leave History
                </h3>
                <p className="text-sm text-gray-500">
                  {employee.name} • Employee ID: {employee.empId}
                </p>
              </div>
              <button
                onClick={() => setShowLeaveHistoryModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="px-6 py-4 overflow-y-auto">
              {leaveHistory.length === 0 ? (
                <p className="text-sm text-gray-500">
                  No leave records available for this employee.
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="text-left text-gray-500 uppercase text-xs tracking-wider border-b">
                        <th className="py-3 pr-4">Type</th>
                        <th className="py-3 pr-4">From</th>
                        <th className="py-3 pr-4">To</th>
                        <th className="py-3 pr-4">Days</th>
                        <th className="py-3 pr-4">Status</th>
                        <th className="py-3 pr-4">Reason</th>
                        <th className="py-3 pr-4">Applied On</th>
                      </tr>
                    </thead>
                    <tbody>
                      {leaveHistory.map((leave) => (
                        <tr
                          key={leave.id}
                          className="border-b last:border-0 hover:bg-gray-50"
                        >
                          <td className="py-3 pr-4 font-medium text-gray-800">
                            {leave.type}
                          </td>
                          <td className="py-3 pr-4 text-gray-700">
                            {leave.startFormatted}
                          </td>
                          <td className="py-3 pr-4 text-gray-700">
                            {leave.endFormatted}
                          </td>
                          <td className="py-3 pr-4 text-gray-700">
                            {leave.totalDays}
                          </td>
                          <td className="py-3 pr-4">
                            <span
                              className={`text-xs font-semibold px-2 py-1 rounded-full ${getStatusBadgeClasses(
                                leave.status
                              )}`}
                            >
                              {leave.status}
                            </span>
                          </td>
                          <td className="py-3 pr-4 text-gray-700">
                            {leave.reason || "—"}
                          </td>
                          <td className="py-3 pr-4 text-gray-700">
                            {leave.appliedOnFormatted}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <div className="border-t px-6 py-3 flex justify-end">
              <button
                onClick={() => setShowLeaveHistoryModal(false)}
                className="px-4 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeProfile;